import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';

import { getInitiativeById } from '@/features/canvas/api';
import {
  deleteBudgetDropdownVisible,
  getBudgetDropdownsVisible,
} from '@/features/canvas/api/budget-dropdown-visible';
import {
  BudgetPopupVisible,
  createBudgetPopupVisible,
  getBudgetPopupsVisible,
} from '@/features/canvas/api/budget-popup-visible';
import {
  createBudgetRoadmapVisible,
  getBudgetRoadmapVisible,
} from '@/features/canvas/api/budget-roadmap-visible';
import {
  createBudgetField,
  getAllBudgetsForRoadmap,
} from '@/features/canvas/api/initiative_budgets';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';
import { useShowToast } from '@/hooks/useShowToast';

interface Props {
  initiativeId?: number;
}

export const useBudgetFields = ({ initiativeId }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showToast } = useShowToast();
  const { roadmap, updateLastSavedTime } = useEditorContext();

  const { mutateAsync: createBudgetPopupVisibleMutation } = useMutation(createBudgetPopupVisible);

  const { mutateAsync: createBudgetRoadmapVisibleMutation } = useMutation(
    createBudgetRoadmapVisible
  );

  const { mutateAsync: deleteBudgetDropdownVisibleMutation } = useMutation(
    deleteBudgetDropdownVisible
  );

  const { mutateAsync: createBudgetFieldMutation } = useMutation(createBudgetField);

  const { mutateAsync: createField, isLoading: isCreatingField } = useMutation(
    async (values: {
      dropdownField: { title: string; type: string; id?: number | undefined } | undefined;
      field: { title: string; type: 'big' | 'small'; text_value?: string; value?: number };
    }) => {
      try {
        console.log('useBudgetFields createField called with:', values);
        const { dropdownField, field } = values;

        if (dropdownField) {
          console.log('Creating field from dropdown:', dropdownField, 'field:', field);
          const newField = {
            title: field.title,
            type: field.type as 'big' | 'small',
          };

          const actions: Promise<unknown>[] = [];
          actions.push(
            createBudgetPopupVisibleMutation({
              roadmapId: roadmap.id!,
              newPopupVisible: newField,
            })
          );

          actions.push(
            createBudgetRoadmapVisibleMutation({
              roadmapId: roadmap.id!,
              newRoadmapVisible: newField,
            })
          );

          actions.push(
            deleteBudgetDropdownVisibleMutation({
              fieldId: dropdownField.id!,
            })
          );

          await Promise.all(actions);
          console.log('Dropdown visibility actions completed');

          // CRITICAL: Sync the actual field data to all initiatives in this roadmap
          // This is what makes the field appear with data, not just the visibility setting
          console.log('Syncing budget fields to all initiatives in roadmap:', roadmap.id);
          try {
            // Call the budget field repository sync method via API
            await fetch(`/api/v1/budget-fields/sync-from-visibles`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
              body: JSON.stringify({
                roadmap_id: roadmap.id,
                field_title: field.title,
                field_type: field.type,
              }),
            });
            console.log('Budget field sync completed successfully');
          } catch (syncError) {
            console.error('Budget field sync failed:', syncError);
            // Don't throw here as the visibility was already set
          }
        } else {
          console.log('Creating regular budget field:', field);
          await createBudgetFieldMutation({
            initiativeId: initiativeId!,
            fieldData: field,
            roadmapId: roadmap.id!,
          });
          console.log('Budget field created successfully');
        }

        // Add a small delay to ensure database operations complete
        await new Promise<void>(resolve => {
          setTimeout(() => resolve(), 100);
        });

        console.log(
          'Starting query invalidations for roadmap:',
          roadmap.id,
          'as Number:',
          Number(roadmap.id)
        );
        await Promise.all([
          queryClient.invalidateQueries([getBudgetPopupsVisible.name, Number(roadmap.id)]),
          queryClient.invalidateQueries([getBudgetRoadmapVisible.name, Number(roadmap.id)]),
          queryClient.invalidateQueries([getBudgetDropdownsVisible.name, Number(roadmap.id)]),
          queryClient.invalidateQueries([getAllBudgetsForRoadmap.name, Number(roadmap.id)]),
          queryClient.invalidateQueries([getInitiativeById.name, initiativeId]),
        ]);
        console.log('Query invalidations completed');

        // Force refetch of budget popup visible query to ensure immediate update
        console.log('Force refetching budget popup visible query');
        void queryClient.refetchQueries([getBudgetPopupsVisible.name, Number(roadmap.id)]);

        // Also try optimistic update to immediately add the new field to cache
        const newVisibleField = {
          id: Date.now(), // Temporary ID
          title: field.title,
          type: field.type as 'big' | 'small',
          roadmap_id: roadmap.id!,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        // Update query cache optimistically
        console.log('Applying optimistic update with field:', newVisibleField);
        queryClient.setQueryData(
          [getBudgetPopupsVisible.name, Number(roadmap.id)],
          (oldData: BudgetPopupVisible[] | undefined) => {
            console.log(
              'Optimistic update - old data:',
              oldData,
              'new data will be:',
              oldData ? [...oldData, newVisibleField] : [newVisibleField]
            );
            return oldData ? [...oldData, newVisibleField] : [newVisibleField];
          }
        );

        updateLastSavedTime();
        showToast('success', t('editor.sidebar.initiative.create_field_success'));
      } catch (e) {
        showToast('error', t('editor.sidebar.initiative.create_field_error'));
      }
    }
  );

  return { createField, isCreatingField };
};
